import React, { useState, useEffect, useCallback, useRef } from 'react';
import classnames from 'classnames';

import './style.scss';

type Event = React.ChangeEvent<HTMLInputElement>;
type KeyEvent = React.KeyboardEvent<HTMLInputElement>;
type InputSize = 'large' | 'default';
export interface InputProps {
  /** 未输入时的占位内容 */
  placeholder?: string;
  /** 自定义样式名 */
  className?: string;
  /** 初始值 */
  defaultValue?: string;
  /** 外部控制input的value值 */
  value?: string;
  /** 可输入的最大值 */
  maxLength?: number;
  /** 输入框规格大小 */
  size?: InputSize;
  /** value改变时的回调 */
  onChange?: (e: Event, value: string) => void;
  /** 输入框聚焦时的回调 */
  onFocus?: () => void;
  /** 输入框失焦时的回调 */
  onBlur?: () => void;
  /** 回车事件 */
  onEnter?: (value: string) => void;
  /** 清除输入框内容时的回调 */
  onClear?: () => void;
  /** 输入框左侧的icon */
  beforeIcon?: JSX.Element;
  /** 输入框右侧的icon */
  afterIcon?: JSX.Element;
  /** input 自动完成属性 */
  autoComplete?: string;
  /** 是否自动选中 */
  autoFocus?: boolean;
  /** input输入类型 */
  type?: string;
  /** 是否支持清除输入内容 */
  allowClear?: boolean;
  /** date-picker中是否有选中值 */
  hasDate?: boolean;
  /** 传入在外部处理的点击事件 */
  handleClick?: () => void;
}

// eslint-disable-next-line max-lines-per-function
export function Input(props: InputProps) {
  const {
    className,
    defaultValue,
    value: propsValue,
    onChange,
    onFocus,
    onBlur,
    onEnter,
    onClear,
    allowClear,
    placeholder,
    maxLength,
    beforeIcon,
    afterIcon,
    autoComplete = '',
    autoFocus = false,
    type = 'text',
    size = 'default',
    hasDate,
    handleClick,
    /** 外部antd之类的hoc会注入字段 */
    ...restProps
  } = props;
  const [value, setValue] = useState(defaultValue || '');
  const [isFocus, setFocus] = useState(false);
  const inputWrapperRef = useRef<any>(null);

  useEffect(() => {
    if (propsValue !== undefined) {
      setValue(propsValue);
    }
  }, [propsValue]);

  const handleChange = (e: Event) => {
    const val = e.target.value;
    setValue(val);
    if (onChange) {
      onChange(e, val);
    }
  };

  const handleFocus = () => {
    setFocus(true);
    if (onFocus) {
      onFocus();
    }
  };

  const handleBlur = () => {
    setFocus(false);
    if (onBlur) {
      onBlur();
    }
  };

  const handleOnKeydown = (e: KeyEvent) => {
    if (onEnter && e.keyCode === 13) {
      onEnter(value);
      handleBlur();
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleClear = useCallback(() => {
    if (allowClear) {
      setValue('');
      if (onClear) {
        onClear();
      }
    }
  }, [allowClear]);

  const handleInputWrapperClick = () => {
    if (handleClick) {
      handleClick();
    }
  };

  return (
    <div
      {...restProps}
      ref={inputWrapperRef}
      onClick={handleInputWrapperClick}
      className={classnames(className, 'sharee-input-wrapper', `sharee-input-wrapper-${size}`, {
        focus: isFocus,
      })}
    >
      {beforeIcon && <div className="sharee-input-before-icon">{beforeIcon}</div>}
      <input
        id="base-input"
        className="sharee-input"
        value={value}
        onChange={handleChange}
        onClick={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleOnKeydown}
        placeholder={placeholder}
        maxLength={maxLength}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        type={type}
      />
      {afterIcon && (
        <div className={classnames('sharee-input-icon', { conceal: !value })} onClick={handleClear}>
          {afterIcon}
        </div>
      )}
    </div>
  );
}
